import { ChevronDown, Plus, Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useLocalization } from "../contexts/LocalizationContext";
import {
  CHAT_CONTEXT_PICK_MODE_EVENT,
  writeDraggedChatContext,
} from "../utils/chatContextDrag";
import HelpQ from "./HelpQ";
import styles from "./AppShell.module.css";

const AppSideRail = ({
  channelItems,
  costItems,
  footerItems,
  shopName,
  handleLogoClick,
  handleNewChat,
  chatSearch,
  setChatSearch,
  previewChatSessions,
  activeChatSessionId,
  openChatSession,
  canShowMoreChatSessions,
  handleShowMoreChat,
  formatSessionTimestamp,
  formatChatSessionMeta,
}) => {
  const { t } = useLocalization();
  const [chatContextPickMode, setChatContextPickMode] = useState(false);
  const [expandedChannelIds, setExpandedChannelIds] = useState(() => {
    const activeParents = channelItems
      .filter((item) => {
        const itemActive =
          typeof item.isActive === "function" ? item.isActive() : false;
        const activeChild = (item.children || []).some((child) =>
          typeof child.isActive === "function" ? child.isActive() : false,
        );
        return itemActive || activeChild;
      })
      .map((item) => item.id);
    return new Set(activeParents);
  });

  const activeParentIds = useMemo(
    () =>
      channelItems
        .filter((item) => {
          const itemActive =
            typeof item.isActive === "function" ? item.isActive() : false;
          const activeChild = (item.children || []).some((child) =>
            typeof child.isActive === "function" ? child.isActive() : false,
          );
          return itemActive || activeChild;
        })
        .map((item) => item.id),
    [channelItems],
  );

  useEffect(() => {
    if (!activeParentIds.length) return;
    setExpandedChannelIds((prev) => {
      const next = new Set(prev);
      activeParentIds.forEach((id) => next.add(id));
      return next;
    });
  }, [activeParentIds]);

  useEffect(() => {
    const handlePickMode = (event) => {
      setChatContextPickMode(Boolean(event?.detail?.enabled));
    };
    window.addEventListener(CHAT_CONTEXT_PICK_MODE_EVENT, handlePickMode);
    return () =>
      window.removeEventListener(CHAT_CONTEXT_PICK_MODE_EVENT, handlePickMode);
  }, []);

  const toggleChannelGroup = (channelId) => {
    setExpandedChannelIds((prev) => {
      const next = new Set(prev);
      if (next.has(channelId)) {
        next.delete(channelId);
      } else {
        next.add(channelId);
      }
      return next;
    });
  };

  return (
    <aside className={styles.sideRail} aria-label={t("AppShell.sideRailAria") || "Channels"}>
      <div
        className={styles.sideRailBrand}
        role="button"
        tabIndex={0}
        onClick={(e) => {
          e.stopPropagation();
          handleLogoClick();
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.stopPropagation();
            handleLogoClick();
          }
        }}
        aria-label={t("AppShell.goHomeAria") || "Go to home"}
      >
        <img
          src="/assets/zeqi_black_clearBG.png"
          alt="Zeqi"
          className={`${styles.sideRailLogo} ${styles.themeAwareLogo}`}
        />
        <div className={styles.sideRailBrandLabel}>
          {/* <span className={styles.sideRailBrandTitle}>Zeqi</span> */}
          <span className={styles.sideRailBrandSubtitle}>{shopName}</span>
        </div>
      </div>

      <div className={styles.sideRailBody} data-tour-scroll-container="true">
        <div className={styles.sideRailSection}>
          <p className={styles.sideRailSectionTitle} data-tour-id="channels-label">
            <span className={styles.sideRailSectionTitleText}>Channels</span>
            <HelpQ stepIndex={7} title="What are channels?" />
          </p>
          <div className={styles.sideRailChannelList}>
            {channelItems.map((item) => {
              const active =
                typeof item.isActive === "function" ? item.isActive() : false;
              const hasChildren = Array.isArray(item.children) && item.children.length > 0;
              const isExpanded = expandedChannelIds.has(item.id);
              const hasActiveChild = hasChildren
                ? item.children.some((child) =>
                    typeof child.isActive === "function" ? child.isActive() : false,
                  )
                : false;
              return (
                <div key={item.id} className={styles.sideRailChannelGroup}>
                  <button
                    type="button"
                    className={`${styles.sideRailChannelItem} ${
                      active || hasActiveChild ? styles.sideRailChannelItemActive : ""
                    } ${item.disabled ? styles.sideRailChannelItemDisabled : ""}`}
                    onClick={item.onClick}
                    disabled={item.disabled}
                    data-tour-id={item.id === "main" ? "main-channel" : undefined}
                  >
                    <span className={styles.sideRailChannelText}>
                      <span className={styles.sideRailChannelHash}>#</span>
                      <span>{item.label}</span>
                    </span>
                    <span className={styles.sideRailChannelTrailing}>
                      {item.badge ? (
                        <span className={styles.sideRailComingSoonBadge}>
                          {item.badge}
                        </span>
                      ) : null}
                      {item.hasIndicator ? (
                        <span className={styles.sideRailChannelDot} aria-hidden="true" />
                      ) : null}
                      {hasChildren ? (
                        <span
                          role="button"
                          tabIndex={0}
                          className={`${styles.sideRailChannelChevron} ${
                            isExpanded ? styles.sideRailChannelChevronExpanded : ""
                          }`}
                          onClick={(event) => {
                            event.stopPropagation();
                            toggleChannelGroup(item.id);
                          }}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              event.stopPropagation();
                              toggleChannelGroup(item.id);
                            }
                          }}
                        >
                          <ChevronDown size={12} strokeWidth={2} aria-hidden="true" />
                        </span>
                      ) : null}
                    </span>
                  </button>
                  {hasChildren && isExpanded ? (
                    <div className={styles.sideRailSubchannelList}>
                      {item.children.map((child) => {
                        const childActive =
                          typeof child.isActive === "function"
                            ? child.isActive()
                            : false;
                        return (
                          <button
                            key={child.id}
                            type="button"
                            className={`${styles.sideRailSubchannelItem} ${
                              childActive ? styles.sideRailSubchannelItemActive : ""
                            } ${child.disabled ? styles.sideRailSubchannelItemDisabled : ""}`}
                            onClick={child.onClick}
                            disabled={child.disabled}
                          >
                            <span className={styles.sideRailSubchannelText}>
                              {child.label}
                            </span>
                            {child.badge ? (
                              <span className={styles.sideRailComingSoonBadge}>
                                {child.badge}
                              </span>
                            ) : null}
                          </button>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>

        <button
          type="button"
          className={styles.sideRailNewChat}
          onClick={handleNewChat}
        >
          <Plus size={16} strokeWidth={2.2} aria-hidden="true" />
          <span>{t("AppShell.newChat") || "New chat with Zeqi"}</span>
        </button>

        <div className={styles.sideRailSection} data-tour-id="costs">
          <p className={styles.sideRailSectionTitle}>
            <span className={styles.sideRailSectionTitleText}>Business costs</span>
            <HelpQ stepIndex={1} title="Why enter costs?" />
          </p>
          <div className={styles.sideRailUtilityList}>
            {costItems.map((item) => {
              const Icon = item.icon;
              const active =
                typeof item.isActive === "function" ? item.isActive() : false;
              return (
                <button
                  key={item.label}
                  type="button"
                  className={`${styles.sideRailUtilityItem} ${
                    active ? styles.sideRailUtilityItemActive : ""
                  }`}
                  onClick={item.onClick}
                >
                  <Icon size={14} strokeWidth={1.8} aria-hidden="true" />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className={`${styles.sideRailSection} ${styles.sideRailChatSection}`}>
          <p className={styles.sideRailSectionTitle}>
            <span className={styles.sideRailSectionTitleText}>
              {t("AppShell.chatHistory") || "Chat History"}
            </span>
          </p>
          <label
            className={styles.sideRailSearch}
            aria-label={t("AppShell.searchConversations") || "Search conversations"}
          >
            <Search size={14} strokeWidth={2.2} aria-hidden="true" />
            <input
              type="text"
              value={chatSearch}
              onChange={(event) => setChatSearch(event.target.value)}
              placeholder={
                t("AppShell.searchConversations") || "Search conversations..."
              }
            />
          </label>
          <div className={styles.sideRailHistoryList}>
            {previewChatSessions.length ? (
              previewChatSessions.map((session) => {
                const dragContext = {
                  key: `chat-session:${session.sessionId}`,
                  label: session.title || session.currentContext?.label || "Chat session",
                  type: "chat_session",
                  sessionId: session.sessionId,
                  collectionName: session.currentContext?.collectionName || null,
                  source: "recent-chat",
                  sourceLabel: "Recent Chat",
                };
                return (
                <button
                  key={session.sessionId}
                  type="button"
                  className={`${styles.sideRailHistoryItem} ${
                    activeChatSessionId === session.sessionId
                      ? styles.sideRailHistoryItemActive
                      : ""
                  } ${chatContextPickMode ? styles.sideRailHistoryItemWiggle : ""}`}
                  onClick={() => openChatSession(session.sessionId)}
                  draggable={chatContextPickMode}
                  onDragStart={
                    chatContextPickMode
                      ? (event) => writeDraggedChatContext(event, dragContext)
                      : undefined
                  }
                  title={
                    chatContextPickMode
                      ? "Drag into Zeqi chat context"
                      : undefined
                  }
                >
                  <span className={styles.sideRailHistoryTitle}>
                    {session.title}
                  </span>
                  <span className={styles.sideRailHistoryPreview}>
                    {session.preview ||
                      t("AppShell.noPreview") ||
                      "No messages yet"}
                  </span>
                  <span className={styles.sideRailHistoryMeta}>
                    {typeof formatChatSessionMeta === "function"
                      ? formatChatSessionMeta(session)
                      : formatSessionTimestamp(
                          session.lastMessageAt || session.lastActivityAt,
                        )}
                  </span>
                </button>
              )})
            ) : (
              <p className={styles.sideRailEmptyState}>
                {chatSearch.trim()
                  ? t("AppShell.noConversationMatches") ||
                    "No matching conversations"
                  : t("AppShell.noConversationsYet") ||
                    "No conversations yet"}
              </p>
            )}
          </div>
          {canShowMoreChatSessions ? (
            <button
              type="button"
              className={styles.sideRailShowMore}
              onClick={handleShowMoreChat}
            >
              {t("AppShell.showMoreConversations") || "Show more"}
            </button>
          ) : null}
        </div>
      </div>

      <div className={styles.sideRailFooter}>
        {footerItems.map((item) => {
          const Icon = item.icon;
          const active =
            typeof item.isActive === "function" ? item.isActive() : false;
          return (
            <div
              key={item.label}
              className={styles.sideRailFooterRow}
              data-tour-id={item.id === "integrations" ? "integrations" : undefined}
            >
              <button
                type="button"
                className={`${styles.sideRailFooterItem} ${
                  active ? styles.sideRailFooterItemActive : ""
                }`}
                onClick={item.onClick}
              >
                <Icon size={14} strokeWidth={1.8} aria-hidden="true" />
                <span>{item.label}</span>
              </button>
              {item.id === "integrations" && (
                <HelpQ stepIndex={0} title="Connect your tools" />
              )}
            </div>
          );
        })}
      </div>
    </aside>
  );
};

export default AppSideRail;
